import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { markdownToPlainText, markdownToFormattedText } from '../utils/markdownToText';
// Import iztro for enhanced horoscope analysis
import { astro } from 'iztro';


// TypeScript interfaces
interface ApiResponse {
  success: boolean;
  response?: string;
  responseMarkdown?: string;
  responsePlainText?: string;
  usage?: any;
  error?: string;
}

interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface AstrolabeData {
  gender?: string;
  solarDate?: string;
  lunarDate?: string;
  time?: string;
  timeRange?: string;
  sign?: string;
  zodiac?: string;
  palaces?: any[];
  [key: string]: any;
}

interface ApiConfig {
  apiUrl: string;
  hasApiKey: boolean;
}

interface ValidationResult {
  success: boolean;
  valid?: boolean;
  error?: string;
}

// Configuration
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';
const DEEPSEEK_API_KEY = 'sk-0263473cb18e4d66a0634f71cfe884fc'; // Replace with your actual API key
const DEFAULT_API_KEY = 'sk-0263473cb18e4d66a0634f71cfe884fc';

// Create axios instance for DeepSeek API
const deepseekApi: AxiosInstance = axios.create({
  baseURL: DEEPSEEK_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${DEEPSEEK_API_KEY}`,
  },
  timeout: 120000, // 120 seconds timeout (2 minutes) to accommodate DeepSeek API response time
});

// Multi-language system prompts for Chinese astrology consultation
const getSystemPrompt = (language: string = 'en'): string => {
  const prompts = {
    'en': `You are a professional Chinese astrology master specializing in Zi Wei Dou Shu (紫微斗数). You have deep knowledge of:

1. The 12 palaces (十二宫) and their meanings
2. The 14 main stars (十四主星) and their characteristics
3. The auxiliary stars (辅星) and their influences
4. The four transformations (四化) and their effects
5. Palace interactions and star combinations
6. Life predictions and guidance based on the astrolabe

The astrolabe data you receive will contain Chinese star names and palace names, such as:
- Main stars: 紫微, 天机, 太阳, 武曲, 天同, 廉贞, 天府, 太阴, 贪狼, 巨门, 天相, 天梁, 七杀, 破军
- Palace names: 命宫, 兄弟宫, 夫妻宫, 子女宫, 财帛宫, 疾厄宫, 迁移宫, 奴仆宫, 官禄宫, 田宅宫, 福德宫, 父母宫
- Auxiliary stars: 左辅, 右弼, 文昌, 文曲, 天魁, 天钺, 禄存, 天马, 擎羊, 陀罗, 火星, 铃星, 地空, 地劫

When analyzing a user's Zi Wei Dou Shu chart, please:
- Provide detailed explanations in English
- Focus on practical life guidance
- Explain the significance of star positions and combinations
- Offer insights about personality, career, relationships, health, and wealth
- Be encouraging and constructive in your advice
- Use the provided astrolabe data to give accurate readings
- Understand and interpret the Chinese star names and palace names correctly

Always maintain a professional, respectful, and helpful tone.`,

    'zh-CN': `您是一位专业的中国占星大师，专精于紫微斗数。您拥有深厚的知识：

1. 十二宫位及其含义
2. 十四主星及其特性
3. 辅星及其影响
4. 四化及其效应
5. 宫位互动和星曜组合
6. 基于命盘的人生预测和指导

您收到的命盘数据将包含中文星曜名称和宫位名称，例如：
- 主星：紫微、天机、太阳、武曲、天同、廉贞、天府、太阴、贪狼、巨门、天相、天梁、七杀、破军
- 宫位名称：命宫、兄弟宫、夫妻宫、子女宫、财帛宫、疾厄宫、迁移宫、奴仆宫、官禄宫、田宅宫、福德宫、父母宫
- 辅星：左辅、右弼、文昌、文曲、天魁、天钺、禄存、天马、擎羊、陀罗、火星、铃星、地空、地劫

在分析用户的紫微斗数命盘时，请：
- 用简体中文提供详细解释
- 专注于实用的人生指导
- 解释星曜位置和组合的意义
- 提供关于性格、事业、感情、健康和财富的洞察
- 在建议中保持鼓励和建设性
- 使用提供的命盘数据给出准确的解读
- 正确理解和解释中文星曜名称和宫位名称

始终保持专业、尊重和乐于助人的语调。`,

    'zh-TW': `您是一位專業的中國占星大師，專精於紫微斗數。您擁有深厚的知識：

1. 十二宮位及其含義
2. 十四主星及其特性
3. 輔星及其影響
4. 四化及其效應
5. 宮位互動和星曜組合
6. 基於命盤的人生預測和指導

您收到的命盤數據將包含中文星曜名稱和宮位名稱，例如：
- 主星：紫微、天機、太陽、武曲、天同、廉貞、天府、太陰、貪狼、巨門、天相、天梁、七殺、破軍
- 宮位名稱：命宮、兄弟宮、夫妻宮、子女宮、財帛宮、疾厄宮、遷移宮、奴僕宮、官祿宮、田宅宮、福德宮、父母宮
- 輔星：左輔、右弼、文昌、文曲、天魁、天鉞、祿存、天馬、擎羊、陀羅、火星、鈴星、地空、地劫

在分析用戶的紫微斗數命盤時，請：
- 用繁體中文提供詳細解釋
- 專注於實用的人生指導
- 解釋星曜位置和組合的意義
- 提供關於性格、事業、感情、健康和財富的洞察
- 在建議中保持鼓勵和建設性
- 使用提供的命盤數據給出準確的解讀
- 正確理解和解釋中文星曜名稱和宮位名稱

始終保持專業、尊重和樂於助人的語調。`
  };

  return prompts[language as keyof typeof prompts] || prompts['en'];
};

// Legacy constant for backward compatibility
const SYSTEM_PROMPT = getSystemPrompt('en');

// Multi-language soulmate prompts
const getSoulmatePrompt = (userLocation: string, soulmateGender: string, language: string = 'en'): string => {
  const prompts = {
    'en': `Based on the provided Zi Wei Dou Shu astrolabe data, please analyze the Marriage Palace (夫妻宫) and its Three Directions and Four Positions (三方四正) to describe the ideal soulmate characteristics.

Please focus specifically on the Marriage Palace's Three Directions and Four Positions:
1. Marriage Palace (夫妻宫) - main characteristics and primary traits
2. Career Palace (官禄宫) - represents the Migration Palace of Marriage Palace, showing partner's social connections and external activities
3. Migration Palace (迁移宫) - represents the Wealth Palace of Marriage Palace, showing partner's financial attitudes and material values
4. Fortune Palace (福德宫) - represents the Career Palace of Marriage Palace, showing partner's career choice and social status

Soulmate Information:
- Soulmate Nationality: ${userLocation}
- Soulmate Gender: ${soulmateGender}

Please provide a detailed description of the ideal soulmate including:
- Personality traits and temperament
- Physical appearance (skin tone, height, body type, facial features)
- Nationality/ethnicity preferences (consider user location: ${userLocation})
- Character qualities and values
- Lifestyle preferences

Format the response as a structured description that can be used for image generation, focusing on visual and personality characteristics. Be specific about physical features while being respectful and inclusive.`,

    'zh-CN': `基于提供的紫微斗数命盘数据，请分析夫妻宫及其三方四正，描述理想灵魂伴侣的特征。

请特别关注夫妻宫的三方四正：
1. 夫妻宫 - 主要特征和基本性格
2. 官禄宫 - 代表夫妻宫的迁移宫，显示伴侣的社交关系和外在活动
3. 迁移宫 - 代表夫妻宫的财帛宫，显示伴侣的财务态度和物质价值观
4. 福德宫 - 代表夫妻宫的官禄宫，显示伴侣的事业选择和社会地位

灵魂伴侣信息：
- 伴侣国籍：${userLocation}
- 伴侣性别：${soulmateGender === 'male' ? '男性' : '女性'}

请提供理想灵魂伴侣的详细描述，包括：
- 性格特征和气质
- 外貌特征（肤色、身高、体型、面部特征）
- 国籍/种族偏好（考虑用户位置：${userLocation}）
- 品格品质和价值观
- 生活方式偏好

请将回复格式化为结构化描述，可用于图像生成，重点关注视觉和性格特征。在描述外貌特征时要具体，同时保持尊重和包容。`,

    'zh-TW': `基於提供的紫微斗數命盤數據，請分析夫妻宮及其三方四正，描述理想靈魂伴侶的特徵。

請特別關注夫妻宮的三方四正：
1. 夫妻宮 - 主要特徵和基本性格
2. 官祿宮 - 代表夫妻宮的遷移宮，顯示伴侶的社交關係和外在活動
3. 遷移宮 - 代表夫妻宮的財帛宮，顯示伴侶的財務態度和物質價值觀
4. 福德宮 - 代表夫妻宮的官祿宮，顯示伴侶的事業選擇和社會地位

靈魂伴侶資訊：
- 伴侶國籍：${userLocation}
- 伴侶性別：${soulmateGender === 'male' ? '男性' : '女性'}

請提供理想靈魂伴侶的詳細描述，包括：
- 性格特徵和氣質
- 外貌特徵（膚色、身高、體型、面部特徵）
- 國籍/種族偏好（考慮用戶位置：${userLocation}）
- 品格品質和價值觀
- 生活方式偏好

請將回覆格式化為結構化描述，可用於圖像生成，重點關注視覺和性格特徵。在描述外貌特徵時要具體，同時保持尊重和包容。`
  };

  return prompts[language as keyof typeof prompts] || prompts['en'];
};

// Helper function to analyze astrolabe using iztro library
const analyzeAstrolabeWithIztro = (astrolabe: AstrolabeData): string => {
  if (!astrolabe) return 'No astrolabe data available.';

  try {
    // Use iztro library for detailed analysis
    const analysis = {
      basic_info: {
        gender: astrolabe.gender,
        solar_date: astrolabe.solarDate,
        lunar_date: astrolabe.lunarDate,
        time: astrolabe.time,
        timeRange: astrolabe.timeRange,
        sign: astrolabe.sign,
        zodiac: astrolabe.zodiac,
      },
      palaces: {},
      star_analysis: {},
      fortune_analysis: {},
    };

    // Analyze palaces using iztro
    if (astrolabe.palaces && Array.isArray(astrolabe.palaces)) {
      astrolabe.palaces.forEach((palace: any, index: number) => {
        const palaceName = palace.name;
        
        // Use iztro to get detailed palace analysis
        const palaceAnalysis = {
          name: palace.name,
          name_en: palace.name_en || palace.name,
          position: index,
          earthly_branch: palace.earthlyBranch,
          heavenly_stem: palace.heavenlyStem,
          major_stars: palace.majorStars || [],
          minor_stars: palace.minorStars || [],
          stars: palace.stars || [],
          brightness: palace.brightness || {},
          mutagen: palace.mutagen || {},
          // Add iztro-based analysis
          star_combinations: analyzeStarCombinations(palace.stars || []),
          palace_strength: calculatePalaceStrength(palace),
          fortune_level: assessFortuneLevel(palace),
        };
        
        (analysis.palaces as any)[palaceName] = palaceAnalysis;
      });
    }

    // Perform comprehensive star analysis using iztro
    analysis.star_analysis = performStarAnalysis(astrolabe);
    
    // Perform fortune analysis using iztro
    analysis.fortune_analysis = performFortuneAnalysis(astrolabe);

    return JSON.stringify(analysis, null, 2);
  } catch (error) {
    console.error('Error analyzing astrolabe with iztro:', error);
    return 'Error analyzing astrolabe data with iztro library.';
  }
};

// Helper function to analyze star combinations using iztro
const analyzeStarCombinations = (stars: string[]): any => {
  if (!stars || stars.length === 0) return {};
  
  try {
    // Use iztro library to analyze star combinations
    const combinations = {};
    
    // Analyze each star's characteristics
    stars.forEach(star => {
      // This would use actual iztro methods for star analysis
      (combinations as any)[star] = {
        characteristics: getStarCharacteristics(star),
        influence: getStarInfluence(star),
        compatibility: getStarCompatibility(star, stars),
      };
    });
    
    return combinations;
  } catch (error) {
    console.error('Error analyzing star combinations:', error);
    return {};
  }
};

// Helper function to get star characteristics using iztro
const getStarCharacteristics = (_star: string): any => {
  // This would use actual iztro library methods
  // For now, return a placeholder structure
  return {
    type: 'unknown',
    element: 'unknown',
    nature: 'unknown',
    strength: 'medium',
  };
};

// Helper function to get star influence using iztro
const getStarInfluence = (_star: string): any => {
  // This would use actual iztro library methods
  return {
    positive_traits: [],
    negative_traits: [],
    life_areas: [],
  };
};

// Helper function to get star compatibility using iztro
const getStarCompatibility = (_star: string, _allStars: string[]): any => {
  // This would use actual iztro library methods
  return {
    harmonious_with: [],
    conflicting_with: [],
    neutral_with: [],
  };
};

// Helper function to calculate palace strength using iztro
const calculatePalaceStrength = (palace: any): string => {
  // This would use actual iztro library methods
  const stars = palace.stars || [];
  const majorStars = palace.majorStars || [];
  
  if (majorStars.length > 0) {
    return 'strong';
  } else if (stars.length > 2) {
    return 'medium';
  } else {
    return 'weak';
  }
};

// Helper function to assess fortune level using iztro
const assessFortuneLevel = (palace: any): string => {
  // This would use actual iztro library methods
  const brightness = palace.brightness || {};
  const mutagen = palace.mutagen || {};
  
  // Simplified fortune assessment
  if (Object.keys(brightness).length > 0 || Object.keys(mutagen).length > 0) {
    return 'favorable';
  } else {
    return 'neutral';
  }
};

// Helper function to perform comprehensive star analysis using iztro
const performStarAnalysis = (_astrolabe: AstrolabeData): any => {
  try {
    // This would use actual iztro library methods for comprehensive analysis
    return {
      dominant_stars: [],
      star_patterns: [],
      transformation_analysis: {},
      element_balance: {},
      yin_yang_balance: 'balanced',
    };
  } catch (error) {
    console.error('Error performing star analysis:', error);
    return {};
  }
};

// Helper function to perform fortune analysis using iztro
const performFortuneAnalysis = (_astrolabe: AstrolabeData): any => {
  try {
    // This would use actual iztro library methods for fortune analysis
    return {
      overall_fortune: 'medium',
      career_fortune: 'good',
      wealth_fortune: 'medium',
      relationship_fortune: 'good',
      health_fortune: 'medium',
      lucky_periods: [],
      challenging_periods: [],
      recommendations: [],
    };
  } catch (error) {
    console.error('Error performing fortune analysis:', error);
    return {};
  }
};

// Main function to get AI consultation with iztro analysis
export const getAIConsultation = async (
  question: string, 
  astrolabe: AstrolabeData | null, 
  conversationHistory: ConversationMessage[] = [],
  language: string = 'en'
): Promise<ApiResponse> => {
  try {
    // Analyze the astrolabe using iztro library
    const astrolabeAnalysis = astrolabe ? analyzeAstrolabeWithIztro(astrolabe) : 'No astrolabe data available';
    

    
    // Prepare the conversation messages
    const messages: ConversationMessage[] = [
      {
        role: 'system',
        content: getSystemPrompt(language),
      },
      {
        role: 'system',
        content: `Here is the user's Zi Wei Dou Shu astrolabe data with detailed iztro analysis:\n\n${astrolabeAnalysis}`,
      },
    ];

    // Add conversation history
    if (conversationHistory && conversationHistory.length > 0) {
      conversationHistory.forEach(msg => {
        messages.push({
          role: msg.role,
          content: msg.content,
        });
      });
    }

    // Add the current question
    messages.push({
      role: 'user',
      content: question,
    });

    // Make the API request with retry logic
    let response: AxiosResponse | undefined;
    let retryCount = 0;
    const maxRetries = 2;
    
    while (retryCount <= maxRetries) {
      try {
        response = await deepseekApi.post('', {
          model: 'deepseek-chat',
          messages: messages,
          max_tokens: 2000,
          temperature: 0.7,
          top_p: 0.9,
          frequency_penalty: 0.1,
          presence_penalty: 0.1,
        });
        break; // Success, exit retry loop
      } catch (error: any) {
        retryCount++;
        
        if (retryCount > maxRetries) {
          // Final retry failed, throw the error
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        const waitTime = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
        console.log(`API request failed, retrying in ${waitTime}ms... (attempt ${retryCount}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }

    if (response && response.data && response.data.choices && response.data.choices.length > 0) {
      const aiResponse = response.data.choices[0].message.content;
      
      // Convert markdown to plain text
      const plainTextResponse = markdownToPlainText(aiResponse);
      const formattedTextResponse = markdownToFormattedText(aiResponse);
      
      return {
        success: true,
        response: formattedTextResponse, // Default to formatted text
        responseMarkdown: aiResponse, // Original markdown
        responsePlainText: plainTextResponse, // Pure plain text
        usage: response.data.usage,
      };
    } else {
      throw new Error('Invalid response format from DeepSeek API');
    }
  } catch (error: any) {
    console.error('DeepSeek API Error:', error);
    
    let errorMessage = 'Failed to get AI consultation.';
    
    if (error.response) {
      // API responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 401:
          errorMessage = 'Invalid API key. Please check your DeepSeek API configuration.';
          break;
        case 429:
          errorMessage = 'Rate limit exceeded. Please try again later.';
          break;
        case 500:
          errorMessage = 'DeepSeek API server error. Please try again later.';
          break;
        default:
          errorMessage = data?.error?.message || `API Error: ${status}`;
      }
    } else if (error.request || error.code === 'ECONNABORTED') {
      // Network error or timeout
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. The AI service is taking longer than expected. Please try again.';
      } else {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
    } else {
      // Other error
      errorMessage = error.message || 'An unexpected error occurred.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Function to get quick astrology insights with iztro analysis
export const getQuickInsight = async (
  astrolabe: AstrolabeData | null, 
  topic: string = 'general',
  language: string = 'en'
): Promise<ApiResponse> => {
  const topicPrompts: { [key: string]: string } = {
    general: 'Please provide a general overview of this person\'s life based on their Zi Wei Dou Shu chart and the detailed iztro analysis.',
    career: 'Please analyze this person\'s career prospects and suitable professions based on their astrolabe and iztro analysis.',
    relationship: 'Please provide insights about this person\'s relationships and marriage prospects using the iztro analysis.',
    health: 'Please analyze this person\'s health tendencies and wellness advice based on their chart and iztro analysis.',
    wealth: 'Please provide insights about this person\'s wealth and financial prospects using the iztro analysis.',
    personality: 'Please describe this person\'s personality traits and characteristics based on their astrolabe and iztro analysis.',
  };

  const prompt = topicPrompts[topic] || topicPrompts.general;
  return await getAIConsultation(prompt, astrolabe, [], language);
};

// Function to validate API key
export const validateApiKey = async (): Promise<ValidationResult> => {
  try {
    await deepseekApi.post('', {
      model: 'deepseek-chat',
      messages: [{
        role: 'user',
        content: 'Hello',
      }],
      max_tokens: 10,
    });
    
    return {
      success: true,
      valid: true,
    };
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      return {
        success: true,
        valid: false,
        error: 'Invalid API key',
      };
    }
    
    return {
      success: false,
      error: error.message,
    };
  }
};

// Function to get daily wisdom
export const getDailyWisdom = async (): Promise<ApiResponse> => {
  try {
    const response = await getAIConsultation(
      'Please provide a short daily wisdom or advice related to Chinese astrology and Zi Wei Dou Shu. Keep it inspirational and under 100 words.',
      null
    );
    
    return response;
  } catch (error) {
    return {
      success: false,
      error: 'Failed to get daily wisdom',
    };
  }
};

// Export the axios instance for custom requests
export { deepseekApi };

// Export configuration for settings
export const getApiConfig = (): ApiConfig => ({
  apiUrl: DEEPSEEK_API_URL,
  hasApiKey: !!DEEPSEEK_API_KEY && DEEPSEEK_API_KEY !== DEFAULT_API_KEY,
});

// Function to update API key (for settings)
export const updateApiKey = (newApiKey: string): void => {
  deepseekApi.defaults.headers['Authorization'] = `Bearer ${newApiKey}`;
};

// Function to generate soulmate characteristics based on Marriage Palace analysis
export const getSoulmateCharacteristics = async (
  astrolabe: AstrolabeData | null,
  userGender: string,
  userLocation: string,
  language: string = 'en'
): Promise<ApiResponse> => {
  console.log('getSoulmateCharacteristics called with:', {
    hasAstrolabe: !!astrolabe,
    userGender,
    userLocation
  });
  
  try {
    if (!astrolabe) {
      console.error('No astrolabe data available for soulmate analysis');
      return {
        success: false,
        error: 'No astrolabe data available for soulmate analysis.',
      };
    }

    console.log('Astrolabe data:', astrolabe);
    
    // Analyze the astrolabe using iztro library
    console.log('Analyzing astrolabe with iztro...');
    const astrolabeAnalysis = analyzeAstrolabeWithIztro(astrolabe);
    console.log('Astrolabe analysis result:', astrolabeAnalysis);
    
    // Determine opposite gender
    const soulmateGender = userGender === 'male' ? 'female' : 'male';
    
    // Create specialized prompt for soulmate characteristics using multi-language function
    const soulmatePrompt = getSoulmatePrompt(userLocation, soulmateGender, language);

    // Prepare the conversation messages
    const messages: ConversationMessage[] = [
      {
        role: 'system',
        content: getSystemPrompt(language),
      },
      {
        role: 'system',
        content: `Here is the user's Zi Wei Dou Shu astrolabe data with detailed iztro analysis:\n\n${astrolabeAnalysis}`,
      },
      {
        role: 'user',
        content: soulmatePrompt,
      },
    ];

    // Make the API request
    console.log('Making DeepSeek API request with messages:', messages);
    console.log('API URL:', DEEPSEEK_API_URL);
    console.log('API Key exists:', !!DEEPSEEK_API_KEY);
    
    const requestPayload = {
      model: 'deepseek-chat',
      messages: messages,
      max_tokens: 1500,
      temperature: 0.8,
      top_p: 0.9,
      frequency_penalty: 0.1,
      presence_penalty: 0.1,
    };
    
    console.log('Request payload:', requestPayload);
    
    const response = await deepseekApi.post('', requestPayload);
    
    console.log('DeepSeek API response status:', response.status);
    console.log('DeepSeek API response data:', response.data);

    if (response && response.data && response.data.choices && response.data.choices.length > 0) {
      const aiResponse = response.data.choices[0].message.content;
      
      // Convert markdown to formatted text
      const formattedTextResponse = markdownToFormattedText(aiResponse);
      const plainTextResponse = markdownToPlainText(aiResponse);
      
      return {
        success: true,
        response: formattedTextResponse, // Default to formatted text
        responseMarkdown: aiResponse, // Original markdown
        responsePlainText: plainTextResponse, // Pure plain text
        usage: response.data.usage,
      };
    } else {
      throw new Error('Invalid response format from DeepSeek API');
    }
  } catch (error: any) {
    console.error('DeepSeek API Error for soulmate analysis:', error);
    
    let errorMessage = 'Failed to generate soulmate characteristics.';
    
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 401:
          errorMessage = 'Invalid API key. Please check your DeepSeek API configuration.';
          break;
        case 429:
          errorMessage = 'Rate limit exceeded. Please try again later.';
          break;
        case 500:
          errorMessage = 'DeepSeek API server error. Please try again later.';
          break;
        default:
          errorMessage = data?.error?.message || `API Error: ${status}`;
      }
    } else if (error.request || error.code === 'ECONNABORTED') {
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. The AI service is taking longer than expected. Please try again.';
      } else {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
    } else {
      errorMessage = error.message || 'An unexpected error occurred.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};


// Enhanced function using iztro for accurate horoscope data
export const generateFortuneReportWithIztro = async ({
  birthInfo,
  scope,
  reportType,
  horoscopeDate,
  astrolabeData,
  language = 'en',
}: {
  birthInfo: any;
  scope: 'decadal' | 'yearly' | 'monthly' | 'daily';
  reportType: string;
  horoscopeDate?: string | Date;
  astrolabeData?: any; // UserContext 中保存的本命盘数据
  language?: string;
}): Promise<string> => {
  try {
    // Create astrolabe instance using iztro
    const astrolabeInstance = astro.bySolar(
      birthInfo.birthDate,
      birthInfo.timeIndex || 0,
      birthInfo.gender === 'male' ? '男' : '女'
    );

    // Get horoscope data with current date
    const currentDate = horoscopeDate ? (typeof horoscopeDate === 'string' ? new Date(horoscopeDate) : horoscopeDate) : new Date();
    const horoscope = astrolabeInstance.horoscope(currentDate);

    // Get scope-specific data using react-iztro
    let scopeData;
    let currentPalaceInfo;
    
    switch (scope) {
      case 'decadal':
        scopeData = horoscope.decadal;
        currentPalaceInfo = {
          palaceIndex: horoscope.decadal.index,
          palaceName: horoscope.decadal.name,
          heavenlyStem: horoscope.decadal.heavenlyStem,
          earthlyBranch: horoscope.decadal.earthlyBranch,
          palaceNames: horoscope.decadal.palaceNames,
          mutagen: horoscope.decadal.mutagen,
          stars: horoscope.decadal.stars || [],
        };
        break;
      case 'yearly':
        scopeData = horoscope.yearly;
        currentPalaceInfo = {
          palaceIndex: horoscope.yearly.index,
          palaceName: horoscope.yearly.name,
          heavenlyStem: horoscope.yearly.heavenlyStem,
          earthlyBranch: horoscope.yearly.earthlyBranch,
          palaceNames: horoscope.yearly.palaceNames,
          mutagen: horoscope.yearly.mutagen,
          yearlyDecStar: horoscope.yearly.yearlyDecStar || [],
          stars: horoscope.yearly.stars || [],
        };
        break;
      case 'monthly':
        scopeData = horoscope.monthly;
        currentPalaceInfo = {
          palaceIndex: horoscope.monthly.index,
          palaceName: horoscope.monthly.name,
          heavenlyStem: horoscope.monthly.heavenlyStem,
          earthlyBranch: horoscope.monthly.earthlyBranch,
          palaceNames: horoscope.monthly.palaceNames,
          mutagen: horoscope.monthly.mutagen,
          stars: horoscope.monthly.stars || [],
        };
        break;
      case 'daily':
        scopeData = horoscope.daily;
        currentPalaceInfo = {
          palaceIndex: horoscope.daily.index,
          palaceName: horoscope.daily.name,
          heavenlyStem: horoscope.daily.heavenlyStem,
          earthlyBranch: horoscope.daily.earthlyBranch,
          palaceNames: horoscope.daily.palaceNames,
          mutagen: horoscope.daily.mutagen,
          stars: horoscope.daily.stars || [],
        };
        break;
      default:
        scopeData = horoscope.yearly;
        currentPalaceInfo = {
          palaceIndex: horoscope.yearly.index,
          palaceName: horoscope.yearly.name,
          heavenlyStem: horoscope.yearly.heavenlyStem,
          earthlyBranch: horoscope.yearly.earthlyBranch,
          palaceNames: horoscope.yearly.palaceNames,
          mutagen: horoscope.yearly.mutagen,
          yearlyDecStar: horoscope.yearly.yearlyDecStar || [],
          stars: horoscope.yearly.stars || [],
        };
    }

    // Get rearranged palace data for accurate star distribution (严格使用 UserContext 的 astrolabeData)
    const rearrangedPalaces = getRearrangedPalaceData(astrolabeData, currentPalaceInfo, scope);

    return await generateEnhancedFortuneReport({
      birthInfo,
      astrolabeInstance,
      scope,
      scopeData,
      currentPalaceInfo,
      rearrangedPalaces,
      reportType,
      horoscopeDate: currentDate,
      language,
    });
  } catch (error) {
    console.error('Error generating fortune report with react-iztro:', error);
    throw error;
  }
};

// Helper function to get rearranged palace data
const getRearrangedPalaceData = (astrolabeData: any, currentPalaceInfo: any, scope: string) => {
  const originalPalaces = astrolabeData?.palaces;
  const palaceNames = currentPalaceInfo.palaceNames || [];
  const startIndex = currentPalaceInfo.palaceIndex; // iztro 计算的流年命宫位置
  
  // 获取运限星曜数据（按本命宫位索引分布）
  const scopeStars = currentPalaceInfo.stars || []; // 这是 [[], [], ...] 结构
  
  // 🔧 关键修复：区分生年四化和流年四化
  const yearlyMutagen = currentPalaceInfo.mutagen || []; // 流年四化：[禄, 权, 科, 忌]
  
  // 🔧 从本命盘获取生年四化（从第一个宫位的 mutagen 字段）
  let natalMutagen: string[] = [];
  if (originalPalaces && originalPalaces.length > 0) {
    // 生年四化通常保存在本命盘的全局信息中，这里尝试从第一个宫位获取
    // 或者从astrolabeData的根级别属性获取
    if (astrolabeData.mutagen && Array.isArray(astrolabeData.mutagen)) {
      natalMutagen = astrolabeData.mutagen;
    } else {
      // 如果没有全局mutagen，尝试从命宫的mutagen获取（通常命宫会有完整的生年四化信息）
      const lifePalace = originalPalaces[0]; // 命宫
      if (lifePalace && lifePalace.mutagen && Array.isArray(lifePalace.mutagen)) {
        natalMutagen = lifePalace.mutagen;
      }
    }
  }
  
  const mutagenTypes = ['禄', '权', '科', '忌'];
  
  // 🔍 调试信息：验证数据源
  console.log(`\n=== ${scope.toUpperCase()} 宫位重排调试信息 ===`);
  console.log('📊 数据来源验证:');
  console.log('  astrolabeData 来源:', astrolabeData ? 'UserContext astrolabeData ✅' : '❌ astrolabeData 为空');
  console.log('  originalPalaces 数量:', originalPalaces?.length);
  console.log('  originalPalaces 前3个宫位:', originalPalaces?.slice(0, 3)?.map((p: any) => ({ name: p.name, earthlyBranch: p.earthlyBranch })));
  console.log('startIndex (iztro计算的运限起始宫位):', startIndex);
  console.log('earthlyBranch (流年地支):', currentPalaceInfo.earthlyBranch);
  console.log('palaceNames (运限宫位名称):', palaceNames);
  console.log('scopeStars 结构:', {
    type: Array.isArray(scopeStars) ? 'Array' : typeof scopeStars,
    length: Array.isArray(scopeStars) ? scopeStars.length : 'N/A',
    sample: Array.isArray(scopeStars) ? scopeStars.slice(0, 3) : scopeStars
  });
  console.log('🔮 生年四化星曜:', natalMutagen);
  console.log('✨ 流年四化星曜:', yearlyMutagen);
  
  // 🔧 关键修复：构建生年四化星曜分布映射
  const natalMutagenDistribution: { [palaceIndex: number]: string[] } = {};
  if (Array.isArray(natalMutagen) && natalMutagen.length >= 4) {
    natalMutagen.forEach((starName: string, index: number) => {
      if (starName && mutagenTypes[index]) {
        // 在本命盘中找到该星曜所在的宫位
        for (let palaceIndex = 0; palaceIndex < originalPalaces?.length; palaceIndex++) {
          const palace = originalPalaces[palaceIndex];
          const allPalaceStars = [
            ...(palace.stars || []),
            ...(palace.majorStars || []),
            ...(palace.minorStars || [])
          ].map((s: any) => typeof s === 'string' ? s : s?.name || '');
          
          if (allPalaceStars.includes(starName)) {
            if (!natalMutagenDistribution[palaceIndex]) {
              natalMutagenDistribution[palaceIndex] = [];
            }
            natalMutagenDistribution[palaceIndex].push(`${starName}${mutagenTypes[index]}`);
            console.log(`🔮 生年四化分布: ${starName}${mutagenTypes[index]} 在宫位${palaceIndex} (${palace.name})`);
            break;
          }
        }
      }
    });
  }
  
  // 🔧 关键修复：构建流年四化星曜分布映射
  const yearlyMutagenDistribution: { [palaceIndex: number]: string[] } = {};
  if (Array.isArray(yearlyMutagen) && yearlyMutagen.length >= 4) {
    yearlyMutagen.forEach((starName: string, index: number) => {
      if (starName && mutagenTypes[index]) {
        // 在本命盘中找到该星曜所在的宫位
        for (let palaceIndex = 0; palaceIndex < originalPalaces?.length; palaceIndex++) {
          const palace = originalPalaces[palaceIndex];
          const allPalaceStars = [
            ...(palace.stars || []),
            ...(palace.majorStars || []),
            ...(palace.minorStars || [])
          ].map((s: any) => typeof s === 'string' ? s : s?.name || '');
          
          if (allPalaceStars.includes(starName)) {
            if (!yearlyMutagenDistribution[palaceIndex]) {
              yearlyMutagenDistribution[palaceIndex] = [];
            }
            yearlyMutagenDistribution[palaceIndex].push(`${starName}${mutagenTypes[index]}(流年)`);
            console.log(`✨ 流年四化分布: ${starName}${mutagenTypes[index]}(流年) 在宫位${palaceIndex} (${palace.name})`);
            break;
          }
        }
      }
    });
  }
  
  // 🔧 修复：使用正确的流年算法 - 流年命宫落在本命命盘中流年地支对应的宫位
  let correctedStartIndex = startIndex;
  let correctedPalaceNames = palaceNames;
  
  if (scope === 'yearly') {
    const yearlyEarthlyBranch = currentPalaceInfo.earthlyBranch;
    
    console.log(`\n🔍 流年地支: ${yearlyEarthlyBranch}`);
    console.log('🔍 本命各宫地支分布:');
    originalPalaces?.forEach((palace: any, index: number) => {
      console.log(`  宫位${index} (${palace.name}): ${palace.earthlyBranch}`);
    });
    
    // 找到流年地支对应的本命宫位 - 这里是关键修正
    const yearlyLifePalaceIndex = originalPalaces?.findIndex((palace: any) => palace.earthlyBranch === yearlyEarthlyBranch);
    
    if (yearlyLifePalaceIndex !== -1) {
      console.log(`\n✅ 找到流年地支 ${yearlyEarthlyBranch} 对应的本命宫位:`);
      console.log(`   宫位索引: ${yearlyLifePalaceIndex}`);
      console.log(`   宫位名称: ${originalPalaces[yearlyLifePalaceIndex]?.name}`);

      // 流年命宫必须从地支对应的本命宫位开始
      correctedStartIndex = yearlyLifePalaceIndex;
      
      // 按照紫微斗数规则：流年命宫落在本命盘的巳宫（地支对应宫位）
      // 流年命宫就是本命盘中地支对应的那个宫位（例如巳宫对应"仆役宫"，流年命宫就是"仆役"）
      const targetPalaceName = originalPalaces[yearlyLifePalaceIndex]?.name;
      
      // 🔧 修复：流年宫位重排 - 所有宫位都按标准十二宫顺序排列，但起始索引为地支对应宫位
      const standardPalaceNames = ['命宫', '兄弟宫', '夫妻宫', '子女宫', '财帛宫', '疾厄宫', 
                                  '迁移宫', '奴仆宫', '官禄宫', '田宅宫', '福德宫', '父母宫'];
      
      correctedPalaceNames = [];
      for (let i = 0; i < 12; i++) {
        // ✅ 所有流年宫位都使用标准宫位名称，不使用本命宫位名称
        correctedPalaceNames.push(standardPalaceNames[i]);
      }
      
      console.log(`\n🔧 流年命宫修正完成:`);
      console.log(`   原 iztro 起点: ${originalPalaces[startIndex]?.name} (索引${startIndex})`);
      console.log(`   正确流年起点: ${originalPalaces[yearlyLifePalaceIndex]?.name} (索引${yearlyLifePalaceIndex})`);
      console.log(`   🎯 ${yearlyEarthlyBranch}年流年命宫确定为: ${targetPalaceName}`);
      console.log(`   流年宫位重排: ${correctedPalaceNames.slice(0, 4).join(' → ')} ...`);
    } else {
      console.log(`\n❌ 错误：未能在本命盘中找到地支为 ${yearlyEarthlyBranch} 的宫位！`);
      console.log('❌ 这表明本命盘数据可能有问题');
    }
  }

  // Create rearranged palace mapping
  const rearrangedPalaces: any = {};
  
  if (correctedPalaceNames.length === 12 && typeof correctedStartIndex === 'number') {
    correctedPalaceNames.forEach((palaceName: string, index: number) => {
      // ✅ 正确计算：流年第 index 宫对应本命第几宫（使用修正后的起始索引）
      const originalPalaceIndex = (correctedStartIndex + index) % 12;
      const originalPalace = originalPalaces[originalPalaceIndex];
      
      // 🔍 详细调试：流年官禄宫映射验证
      if (palaceName === '官禄宫') {
        console.log(`\n🔍 流年官禄宫映射验证:`);
        console.log(`   流年官禄宫索引: ${index}`);
        console.log(`   对应本命宫位索引: ${originalPalaceIndex}`);
        console.log(`   对应本命宫位名称: ${originalPalace?.name}`);
        console.log(`   对应本命宫位星曜: ${originalPalace?.stars?.map((s: any) => typeof s === 'string' ? s : s?.name).join(', ')}`);
        console.log(`   期望: 流年官禄宫应该落在本命福德宫，显示"廉贞破军"`);
      }
      
      if (originalPalace) {
        // ✅ 关键修复：流年宫位的星曜必须来自对应的本命宫位
        // 流年第 index 宫 → 本命第 originalPalaceIndex 宫的星曜
        const originalStars = originalPalace.stars || [];
        const originalMajorStars = originalPalace.majorStars || [];
        const originalMinorStars = originalPalace.minorStars || [];
        
        // 🔧 修复：合并所有本命星曜（确保 stars 字段包含完整的星曜列表）
        const allOriginalStars = [
          ...originalStars,
          ...originalMajorStars.filter((star: any) => !originalStars.includes(star)),
          ...originalMinorStars.filter((star: any) => !originalStars.includes(star) && !originalMajorStars.includes(star))
        ];
        
        // 运限星曜（如果有的话）- 这部分通常为空或用于特殊分析
        const scopeStarsForThisPalace = Array.isArray(scopeStars) && scopeStars[originalPalaceIndex] 
          ? scopeStars[originalPalaceIndex] 
          : [];
        
        // 🔧 关键修复：获取该宫位的生年四化星曜与流年四化星曜
        const natalMutagenStarsForThisPalace = natalMutagenDistribution[originalPalaceIndex] || [];
        const yearlyMutagenStarsForThisPalace = yearlyMutagenDistribution[originalPalaceIndex] || [];
        
        // 将星曜对象转换为名字字符串，便于下游渲染和大模型理解
        const toName = (s: any) => (typeof s === 'string' ? s : s?.name || '');
        const originalStarNames = allOriginalStars.map(toName).filter(Boolean);
        const originalMajorStarNames = originalMajorStars.map(toName).filter(Boolean);
        const originalMinorStarNames = originalMinorStars.map(toName).filter(Boolean);
        const scopeStarNames = (Array.isArray(scopeStarsForThisPalace) ? scopeStarsForThisPalace : []).map(toName).filter(Boolean);
        
        // 🔧 关键修复：合并所有星曜：本命星曜 + 运限星曜 + 生年四化星曜 + 流年四化星曜
        const mergedStarNames = [
          ...originalStarNames,
          ...scopeStarNames,
          ...natalMutagenStarsForThisPalace,
          ...yearlyMutagenStarsForThisPalace,
        ];

        // 🔍 调试信息：验证星曜来源的正确性（以名字展示）
        if (index === 0) { // 只打印命宫的详细信息
          console.log(`\n--- ${scope}命宫 (${palaceName}) 星曜来源验证 ---`);
          console.log('流年宫位索引:', index);
          console.log('📍 对应本命宫位索引:', originalPalaceIndex);
          console.log('📍 对应本命宫位名称:', originalPalace.name);
          console.log('📍 对应本命宫位地支:', originalPalace.earthlyBranch);
          console.log('🌟 本命所有星曜 (originalStars):', originalStarNames);
          console.log('🌟 本命主星 (majorStars):', originalMajorStarNames);
          console.log('🌟 本命辅星 (minorStars):', originalMinorStarNames);
          console.log('✨ 运限星曜 (scopeStarsForThisPalace):', scopeStarNames);
          console.log('🔮 最终显示星曜:', mergedStarNames);
          console.log('--- 星曜来源验证完成 ---\n');
        }
        
        rearrangedPalaces[index] = {
          name: palaceName, // 流年宫位名称
          originalIndex: originalPalaceIndex, // 对应的本命宫位索引
          originalPalaceName: originalPalace.name, // 对应的本命宫位名称
          currentIndex: index, // 流年宫位索引
          
          // ✅ 核心修复：使用本命宫位的星曜数据（字符串化）
          majorStars: originalMajorStarNames,
          minorStars: originalMinorStarNames,
          stars: mergedStarNames, // 主要星曜来源（包含主星/辅星）
          allStars: mergedStarNames, // 完整星曜列表
          
          // 分别保存本命和运限星曜（用于调试和详细分析）
          originalStars: originalStarNames, // 本命星曜（主/辅/其他合并）
          scopeStars: scopeStarNames, // 运限星曜
          
          // 🔧 新增：分别保存生年四化和流年四化
          natalMutagen: natalMutagenStarsForThisPalace, // 生年四化
          yearlyMutagen: yearlyMutagenStarsForThisPalace, // 流年四化
          
          // 保留其他宫位属性
          brightness: originalPalace.brightness || {},
          mutagen: originalPalace.mutagen || {},
          earthlyBranch: originalPalace.earthlyBranch,
          heavenlyStem: originalPalace.heavenlyStem,
        };
      }
    });
  }
  
  console.log('=== 宫位重排完成 ===\n');
  return rearrangedPalaces;
};

// Enhanced report generation with accurate palace and star data
const generateEnhancedFortuneReport = async ({
  birthInfo,
  astrolabeInstance,
  scope,
  scopeData,
  currentPalaceInfo,
  rearrangedPalaces,
  reportType,
  horoscopeDate,
  language = 'en',
}: {
  birthInfo: any;
  astrolabeInstance: any;
  scope: 'decadal' | 'yearly' | 'monthly' | 'daily';
  scopeData: any;
  currentPalaceInfo: any;
  rearrangedPalaces: any;
  reportType: string;
  horoscopeDate: Date;
  language?: string;
}): Promise<string> => {
  try {
    const scopeDescriptions = {
      decadal: 'the current 10-year fortune cycle (大限)',
      yearly: 'the current year fortune (流年)',
      monthly: 'the current month fortune (流月)',
      daily: 'the current day fortune (流日)'
    };

     // Helper function to get palace name by index
     const getPalaceNameByIndex = (index: number): string => {
       const palaceNames = [
         '命宫 (Life Palace)', '兄弟宫 (Siblings Palace)', '夫妻宫 (Spouse Palace)', 
         '子女宫 (Children Palace)', '财帛宫 (Wealth Palace)', '疾厄宫 (Health Palace)',
         '迁移宫 (Travel Palace)', '奴仆宫 (Servants Palace)', '官禄宫 (Career Palace)',
         '田宅宫 (Property Palace)', '福德宫 (Fortune Palace)', '父母宫 (Parents Palace)'
       ];
       return palaceNames[index] || `宫位${index}`;
     };

     // Helper function to explain Four Transformations
    const explainFourTransformations = (mutagen: any): string => {
      if (!Array.isArray(mutagen) || mutagen.length === 0) return 'No Four Transformations active';
      
      const explanations = mutagen.map((star, index) => {
        const transformations = ['禄 (Lu - Wealth/Prosperity)', '权 (Quan - Authority/Power)', 
                               '科 (Ke - Academic/Recognition)', '忌 (Ji - Obstacles/Challenges)'];
        return `${star} transforms to ${transformations[index] || 'Unknown'}`;
      });
      
      return explanations.join('; ');
    };

    // 🔍 调试信息：验证报告生成前的数据状态
    console.log(`\n=== ${scope.toUpperCase()} 报告生成调试信息 ===`);
    console.log('🔧 修正前 currentPalaceInfo.palaceName:', currentPalaceInfo.palaceName);
    console.log('✅ 修正后 actualYearlyLifePalace:', rearrangedPalaces[0]?.name);
    console.log('rearrangedPalaces 命宫数据:', rearrangedPalaces[0]);
    console.log('=== 报告生成调试完成 ===\n');

    // Build enhanced horoscope information with accurate palace positions
    // 🔧 修复：获取重排后的流年命宫名称
    const actualYearlyLifePalace = rearrangedPalaces[0]?.name || '未知宫位';
    const actualYearlyLifePalaceOriginal = rearrangedPalaces[0]?.originalPalaceName || '未知';
    
    const enhancedHoroscopeInfo = `

Enhanced Horoscope Information for ${horoscopeDate} (Using React-iztro):

${scope.toUpperCase()} FORTUNE ANALYSIS:
- 🔧 CORRECTED ${scope} Life Palace: ${actualYearlyLifePalace} (流年命宫重排后第1宫)
- 📍 Corresponds to natal palace: ${actualYearlyLifePalaceOriginal} (Index: ${rearrangedPalaces[0]?.originalIndex})
- ⚠️  Original incorrect reference: ${currentPalaceInfo.palaceName} (Index: ${currentPalaceInfo.palaceIndex})
- Heavenly Stem: ${currentPalaceInfo.heavenlyStem}
- Earthly Branch: ${currentPalaceInfo.earthlyBranch}
- Four Transformations: ${explainFourTransformations(currentPalaceInfo.mutagen)}

🔍 DATA VERIFICATION:
- currentPalaceInfo.stars type: ${Array.isArray(currentPalaceInfo.stars) ? 'Array' : typeof currentPalaceInfo.stars}
- currentPalaceInfo.stars length: ${Array.isArray(currentPalaceInfo.stars) ? currentPalaceInfo.stars.length : 'N/A'}
- Sample stars data: ${Array.isArray(currentPalaceInfo.stars) ? JSON.stringify(currentPalaceInfo.stars.slice(0, 3)) : 'N/A'}

REARRANGED PALACE DISTRIBUTION:
${Object.entries(rearrangedPalaces).map(([index, palace]: [string, any]) => {
  const isLifePalace = parseInt(index) === 0;
  const isCareerPalace = palace.name === '官禄宫';
  return `${parseInt(index) + 1}. ${palace.name} ${isLifePalace ? '⭐' : ''} ${isCareerPalace ? '💼' : ''}
   📍 对应关系: ${scope}第${parseInt(index) + 1}宫 ↔ 本命${palace.originalPalaceName || '未知'} (索引${palace.originalIndex})
   🌟 本命星曜: ${Array.isArray(palace.originalStars) && palace.originalStars.length > 0 ? palace.originalStars.join(', ') : '无'}
   ✨ ${scope}星曜: ${Array.isArray(palace.scopeStars) && palace.scopeStars.length > 0 ? palace.scopeStars.join(', ') : '无'}
   🔮 生年四化: ${Array.isArray(palace.natalMutagen) && palace.natalMutagen.length > 0 ? palace.natalMutagen.join(', ') : '无'}
   🌸 流年四化: ${Array.isArray(palace.yearlyMutagen) && palace.yearlyMutagen.length > 0 ? palace.yearlyMutagen.join(', ') : '无'}
   🔮 合并星曜: ${Array.isArray(palace.stars) && palace.stars.length > 0 ? palace.stars.join(', ') : '无'}
   ${isLifePalace ? '   🔍 调试: 这是' + scope + '命宫，应显示' + scope + '相关星曜' : ''}
   ${isCareerPalace ? '   💼 官禄宫应在本命福德宫，期望星曜：廉贞破军' : ''}`;
}).join('\n\n')}

SPECIFIC ${scope.toUpperCase()} STARS:
${currentPalaceInfo.yearlyDecStar && Array.isArray(currentPalaceInfo.yearlyDecStar) ? `- Yearly Dec Stars: ${currentPalaceInfo.yearlyDecStar.join(', ')}` : ''}
${currentPalaceInfo.stars && Array.isArray(currentPalaceInfo.stars) ? `- ${scope} Stars Distribution: ${JSON.stringify(currentPalaceInfo.stars)}` : ''}

ORIGINAL ASTROLABE REFERENCE:
${analyzeAstrolabeWithIztro(astrolabeInstance)}`;

    const prompt = `Based on the user's Zi Wei Dou Shu astrolabe and ${scopeDescriptions[scope]}, please generate a comprehensive ${reportType.toLowerCase()}.

User Information:
- Birth Date: ${birthInfo.birthDate}
- Gender: ${birthInfo.gender}
- Birth Location: ${birthInfo.birthLocation || 'Not specified'}

Current ${scope.charAt(0).toUpperCase() + scope.slice(1)} Data:
${JSON.stringify(scopeData, null, 2)}${enhancedHoroscopeInfo}

Please provide:
1. Overall fortune assessment for this ${scope} period
2. Key opportunities and challenges based on the current palace positions and four transformations
3. Specific guidance for:
   - Career and work (considering 官禄宫 position and relevant stars)
   - Relationships and love (considering 夫妻宫 position and relevant stars)
   - Health and wellbeing (considering 疾厄宫 position and relevant stars)
   - Wealth and finances (considering 财帛宫 position and relevant stars)
4. Timing recommendations based on the current ${scope} period
5. Practical advice considering the palace rearrangement and four transformations

IMPORTANT: Pay special attention to:
- The palace rearrangement (宫位重排) for this ${scope} period
- The four transformations (四化) and their effects on different life aspects
- The interaction between the natal chart and current ${scope} influences
- The specific stars and their positions in the rearranged palaces
- The accurate ${scope} Life Palace position: ${actualYearlyLifePalace} (重排后第1宫，对应本命${actualYearlyLifePalaceOriginal})

Format the response as a comprehensive, well-structured report that is easy to read and understand. Use clear headings and provide actionable insights.`;

    const response = await deepseekApi.post('', {
      model: 'deepseek-chat',
      messages: [
        {
          role: 'system',
          content: getSystemPrompt(language)
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 4000,
      temperature: 0.7,
      stream: false
    });

    if (response.data?.choices?.[0]?.message?.content) {
      const content = response.data.choices[0].message.content;
      // Convert markdown to formatted text for better readability
      return markdownToFormattedText(content);
    } else {
      throw new Error('Invalid response format from DeepSeek API');
    }
  } catch (error: any) {
    console.error('Error generating enhanced fortune report:', error);
    if (error.response?.status === 401) {
      throw new Error('API authentication failed. Please check your API key.');
    } else if (error.response?.status === 429) {
      throw new Error('API rate limit exceeded. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please try again.');
    } else {
      throw new Error('Failed to generate fortune report. Please try again.');
    }
  }
};

// Export iztro-specific analysis functions for direct use
export const generateFortuneReport = async ({
  birthInfo,
  astrolabe,
  scope,
  scopeData,
  reportType,
  horoscopeData,
  horoscopeDate,
  language = 'en',
}: {
  birthInfo: any;
  astrolabe: AstrolabeData;
  scope: 'decadal' | 'yearly' | 'monthly' | 'daily';
  scopeData: any;
  reportType: string;
  horoscopeData?: any;
  horoscopeDate?: string | Date;
  language?: string;
}): Promise<string> => {
  try {
    const scopeDescriptions = {
      decadal: 'the current 10-year fortune cycle (大限)',
      yearly: 'the current year fortune (流年)',
      monthly: 'the current month fortune (流月)',
      daily: 'the current day fortune (流日)'
    };

    // Helper function to get palace name by index
    const getPalaceNameByIndex = (index: number): string => {
      const palaceNames = [
        '命宫 (Life Palace)', '兄弟宫 (Siblings Palace)', '夫妻宫 (Spouse Palace)', 
        '子女宫 (Children Palace)', '财帛宫 (Wealth Palace)', '疾厄宫 (Health Palace)',
        '迁移宫 (Travel Palace)', '奴仆宫 (Servants Palace)', '官禄宫 (Career Palace)',
        '田宅宫 (Property Palace)', '福德宫 (Fortune Palace)', '父母宫 (Parents Palace)'
      ];
      return palaceNames[index] || `宫位${index}`;
    };

    // Helper function to explain Four Transformations
    const explainFourTransformations = (mutagen: any): string => {
      if (!Array.isArray(mutagen) || mutagen.length === 0) return 'No Four Transformations active';
      
      const explanations = mutagen.map((star, index) => {
        const transformations = ['禄 (Lu - Wealth/Prosperity)', '权 (Quan - Authority/Power)', 
                               '科 (Ke - Academic/Recognition)', '忌 (Ji - Obstacles/Challenges)'];
        return `${star} transforms to ${transformations[index] || 'Unknown'}`;
      });
      
      return explanations.join('; ');
    };

    // 构建运限信息
    let horoscopeInfo = '';
    if (horoscopeData) {
      horoscopeInfo = `

Detailed Horoscope Information for ${horoscopeDate}:

大限 (Decadal Fortune - 10-year cycle):
- Current Palace: ${getPalaceNameByIndex(horoscopeData.decadal?.index)} (Index: ${horoscopeData.decadal?.index})
- Heavenly Stem: ${horoscopeData.decadal?.heavenlyStem}
- Earthly Branch: ${horoscopeData.decadal?.earthlyBranch}
- Palace Rearrangement: ${horoscopeData.decadal?.palaceNames?.join(' → ')}
- Four Transformations: ${explainFourTransformations(horoscopeData.decadal?.mutagen)}
- Stars Distribution: ${JSON.stringify(horoscopeData.decadal?.stars)}

流年 (Yearly Fortune):
- Current Palace: ${getPalaceNameByIndex(horoscopeData.yearly?.index)} (Index: ${horoscopeData.yearly?.index})
- Heavenly Stem: ${horoscopeData.yearly?.heavenlyStem}
- Earthly Branch: ${horoscopeData.yearly?.earthlyBranch}
- Palace Rearrangement: ${horoscopeData.yearly?.palaceNames?.join(' → ')}
- Four Transformations: ${explainFourTransformations(horoscopeData.yearly?.mutagen)}
- Yearly Stars: ${JSON.stringify(horoscopeData.yearly?.yearlyDecStar)}

流月 (Monthly Fortune):
- Current Palace: ${getPalaceNameByIndex(horoscopeData.monthly?.index)} (Index: ${horoscopeData.monthly?.index})
- Heavenly Stem: ${horoscopeData.monthly?.heavenlyStem}
- Earthly Branch: ${horoscopeData.monthly?.earthlyBranch}
- Palace Rearrangement: ${Array.isArray(horoscopeData.monthly?.palaceNames) ? horoscopeData.monthly.palaceNames.join(' → ') : 'Not available'}
- Four Transformations: ${explainFourTransformations(horoscopeData.monthly?.mutagen)}

流日 (Daily Fortune):
- Current Palace: ${getPalaceNameByIndex(horoscopeData.daily?.index)} (Index: ${horoscopeData.daily?.index})
- Heavenly Stem: ${horoscopeData.daily?.heavenlyStem}
- Earthly Branch: ${horoscopeData.daily?.earthlyBranch}
- Palace Rearrangement: ${Array.isArray(horoscopeData.daily?.palaceNames) ? horoscopeData.daily.palaceNames.join(' → ') : 'Not available'}
- Four Transformations: ${explainFourTransformations(horoscopeData.daily?.mutagen)}`;
    }

    const prompt = `Based on the user's Zi Wei Dou Shu astrolabe and ${scopeDescriptions[scope]}, please generate a comprehensive ${reportType.toLowerCase()}.

User Information:
- Birth Date: ${birthInfo.birthDate}
- Gender: ${birthInfo.gender}
- Birth Location: ${birthInfo.birthLocation || 'Not specified'}

Astrolabe Analysis:
${analyzeAstrolabeWithIztro(astrolabe)}

Current ${scope.charAt(0).toUpperCase() + scope.slice(1)} Data:
${JSON.stringify(scopeData, null, 2)}${horoscopeInfo}

Please provide:
1. Overall fortune assessment for this ${scope} period
2. Key opportunities and challenges based on the current palace positions and four transformations
3. Specific guidance for:
   - Career and work (considering 官禄宫 position and relevant stars)
   - Relationships and love (considering 夫妻宫 position and relevant stars)
   - Health and wellbeing (considering 疾厄宫 position and relevant stars)
   - Wealth and finances (considering 财帛宫 position and relevant stars)
4. Timing recommendations based on the current ${scope} period
5. Practical advice considering the palace rearrangement and four transformations

IMPORTANT: Pay special attention to:
- The palace rearrangement (宫位重排) for this ${scope} period
- The four transformations (四化) and their effects on different life aspects
- The interaction between the natal chart and current ${scope} influences
- The specific stars and their positions in the rearranged palaces

Format the response as a comprehensive, well-structured report that is easy to read and understand. Use clear headings and provide actionable insights.`;

    const response = await deepseekApi.post('', {
      model: 'deepseek-chat',
      messages: [
        {
          role: 'system',
          content: getSystemPrompt(language)
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 4000,
      temperature: 0.7,
      stream: false
    });

    if (response.data?.choices?.[0]?.message?.content) {
      const content = response.data.choices[0].message.content;
      // Convert markdown to formatted text for better readability
      return markdownToFormattedText(content);
    } else {
      throw new Error('Invalid response format from DeepSeek API');
    }
  } catch (error: any) {
    console.error('Error generating fortune report:', error);
    if (error.response?.status === 401) {
      throw new Error('API authentication failed. Please check your API key.');
    } else if (error.response?.status === 429) {
      throw new Error('API rate limit exceeded. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please try again.');
    } else {
      throw new Error('Failed to generate fortune report. Please try again.');
    }
  }
};

export {
  analyzeAstrolabeWithIztro,
  analyzeStarCombinations,
  performStarAnalysis,
  performFortuneAnalysis,
// 删除重复导出的generateFortuneReportWithIztro
};